import { BetterSupportIcon, FeatureParityIcon, HonestPricingIcon, NoLockInIcon, TelemetryPatternIcon, ZeroConfigIcon } from "../icons-v2";
import type {
  ComparisonSection,
  ComparisonTableRow,
  SwitchStatsSection,
} from "./comparison-types";

export type {
  ComparisonColumnContent,
  ComparisonSection,
  ComparisonTableCell,
  ComparisonTableRow,
  PricingRow,
  SwitchStatItem,
  SwitchStatsSection,
} from "./comparison-types";

export const DYNATRACE_COMPARISON_SECTIONS: ComparisonSection[] = [
  {
    id: "full-stack-observability",
    title: "Full-Stack Observability",
    middleware: {
      description:
        "APM, Infrastructure, Logs, Distributed Tracing, RUM, Synthetic Monitoring, Databases, Containers, Kubernetes, Serverless, and LLM Observability. If you can monitor it in Dynatrace, you can monitor it here.",
      bullets: [
        "Every pillar included, one plan",
        'OpenTelemetry-native, not bolted-on',
        "OpsAI included, not a per-host add-on",
      ],
      image: "/images/comparison/feature-parity-middleware.webp"
    },
    competitor: {
      description:
        "Full-Stack Monitoring, Log Management, RUM, Synthetic, Application Security, and Kubernetes each consume separate DPS capability units. You build the bundle; DPS bills it dimension by dimension.",
      bullets: [
        "Per-capability DPS units",
        "Davis AI billed on top (~$3.60/host/month)",
        "Grail lakehouse for logs, priced per GiB-day",
      ],
      image: "/images/comparison/full-stack-obs-dynatraces.webp"
    },
    icon: FeatureParityIcon,
  },
  {
    id: "no-lock-in",
    title: "No vendor lock-in",
    middleware: {
      description:
        "Middleware built on OpenTelemetry from day one. Portable instrumentation, no proprietary agent, and one timeline where logs, metrics, traces, and events correlate automatically.",
      bullets: [
        "Standard OTel collectors, no fork",
        "Cloud, on-prem, BYOC, or hybrid",
        "Switch vendors without re-instrumenting",
      ],
      image: "/images/comparison/no-lock-in-middleware.webp"
    },
    competitor: {
      description:
        "Davis AI's causal analysis runs on Smartscape, which is built from OneAgent data. Moving off means uninstalling OneAgent everywhere and re-instrumenting your fleet.",
      bullets: [
        "OneAgent required for full functionality",
        "Davis reasons only over OneAgent data",
        "Migration means agent rip-and-replace",
      ],
      image: "/images/comparison/no-vendor-dynatrace.webp"
    },
    icon: NoLockInIcon,
  },
  {
    id: "better-support",
    title: "Better support and experience",
    middleware: {
      description:
        "Every Middleware customer gets responsive, hands-on support, including a 1-hour SLA for critical issues. Dedicated shared Slack and Teams channels connect you directly with the engineers who built the product.",
      bullets: [
        "1-hour critical-issue response SLA",
        "Direct Slack / Teams channels",
        "Self-serve signup, no mandatory sales motion",
      ],
    },
    competitor: {
      description:
        "After the 15-day trial, customers typically transition to a sales-led process with a required annual commitment of 1–3 years. Access to support tiers is based on the contract value.",
      bullets: [
        "15-day trial, no permanent free tier",
        "Median enterprise contract ~$182K/year",
        "Support tied to contract tier",
      ],
    },
    icon: BetterSupportIcon,
  },
  {
    id: "honest-pricing",
    title: "Honest Pricing",
    middleware: {
      description:
        "Simple, transparent pricing based on the data you actually send. There are no surprise charges for monthly traffic spikes, and OpsAI is bundled into the platform instead of being billed per host.",
      bullets: [
        "Pay per GB of data sent",
        "OpsAI included with token-based price",
        "180-day log retention bundled",
      ],
    },
    competitor: {
      description:
        "DPS bills across seven+ dimensions at once: memory-GiB-hours, host-hours, pod-hours, log ingest, log retention, RUM sessions, plus Davis AI. 15-minute rounding inflates ephemeral workload costs 2–4x.",
      bullets: [
        "7+ overlapping meters",
        "15-min rounding, 4-GiB memory minimum",
        "Davis AI billed separately",
      ],
    },
    icon: HonestPricingIcon,
  },
  {
    id: "zero-config",
    title: "Zero Configuration",
    middleware: {
      description:
        "One install script, a lightweight OpenTelemetry Collector, and full-stack visibility in under a minute. Auto-instrumentation, service discovery, and Ingestion Control to drop noise in two clicks.",
      bullets: [
        "One-line install, no implementation engineer",
        "Auto service + dependency discovery",
        "No proprietary query language to learn",
      ],
      image: "/images/comparison/zero-config-middleware.webp"
    },
    competitor: {
      description:
        "OneAgent rollouts run multi-day to multi-week. Grail queries use DQL, Dynatrace's proprietary language. DPS budgeting per capability, per environment, is required to keep costs sane.",
      bullets: [
        "Multi-day to multi-week OneAgent rollouts",
        "DQL learning curve for Grail",
        "Manual DPS budgeting per capability",
      ],
      image: "/images/comparison/zero-config-dynatraces.webp"
    },
    icon: ZeroConfigIcon,
  },
  {
    id: "telemetry-patterns",
    title: "Telemetry Patterns",
    middleware: {
      description:
        'Service-level alerting on real metrics. Search every span you have sent. Click any log, jump to the trace. Slice by any high-cardinality dimension without watching a DDU meter climb.',
      bullets: [
        "Service-level RED metrics on every operation",
        "Full-history span search, not last 15 minutes",
        "Logs-to-traces correlation for OTel data",
      ],
    },
    competitor: {
      description:
        'Powerful once fully configured, but getting there often requires careful tuning. Analysis is further constrained, making it harder to investigate data freely without considering cost and retention limits.',
      bullets: [
        "Capability-unit consumption on every query",
        "Hot/warm/cold Grail tiers for logs",
        "OneAgent scope limits AI coverage",
      ],
    },
    icon: TelemetryPatternIcon,
  },
];

export const DYNATRACE_FEATURE_TABLE_ROWS: ComparisonTableRow[] = [
  {
    id: "bottom-line",
    label: "Bottom line",
    subtitle: "Who each tool is really built for",
    middleware: {
      text: "Cost-conscious, OTel-native, cloud-native teams",
    },
    competitor: {
      text: "Enterprises deep in the Dynatrace + OneAgent ecosystem",
    },
  },
  {
    id: "sre-agent",
    label: "SRE Agent ",
    subtitle: "(detect → fix → PR)",
    middleware: {
      status: "check",
      description:
        "OpsAI",
    },
    competitor: {
      status: "cross",
      description: "Davis AI  RCA only",
    },
  },
  {
    id: "auto-remediation",
    label: "Auto-remediation ",
    subtitle: "(code + K8s)",
    middleware: {
      status: "check",
      description:
        "Supported",
    },
    competitor: {
      status: "cross",
    },
  },
  {
    id: "ai-on-third-party-data",
    label: "AI on third-party data",
    middleware: {
      status: "check",
      description: "Datadog, Grafana, Dynatrace",
    },
    competitor: {
      status: "cross",
      description:
        "OneAgent data only",
    },
  },
  {
    id: "byoc-on-prem",
    label: "BYOC / on-prem",
    middleware: {
      status: "check",
      description: "Filter and shape metrics at ingest to control spend.",
    },
    competitor: {
      status: "cross",
      description: "Grail-managed",
    },
  },
  {
    id: "value-for-money",
    label: "Value for money",
    middleware: {
      description:
        "3–5× more cost-effective",
    },
    competitor: {
      description: '~$182K/yr median enterprise contract',
    },
  },
  {
    id: "instrumentation",
    label: "Instrumentation",
    middleware: {
      description:
        "OpenTelemetry-native",
    },
    competitor: {
      description: 'OneAgent (proprietary)',
    },
  },
  {
    id: "query-language",
    label: "Query language",
    middleware: {
      description:
        "Unified, no proprietary DSL",
    },
    competitor: {
      description: 'DQL',
    },
  },
  {
    id: "ingestion-pipeline",
    label: "Ingestion pipeline",
    middleware: {
      description:
        "One alert covers the service, not a pile of endpoints.",
    },
    competitor: {
      description: 'Can\'t alert representatively at the service level.',
    },
  },
  {
    id: "high-cardinality-metrics",
    label: "Custom / high-cardinality metrics",
    middleware: {
      status: "check",
      description: "Bundled",
    },
    competitor: {
      status: "cross",
      description: "Consume DPS units",
    },
  },
  {
    id: "ai-pricing",
    label: "AI pricing",
    middleware: {
      status: "check",
      description: "Included",
    },
    competitor: {
      headline: "Billed differently",
      headlineStyle: "bold",
      description: "Davis AI ~$3.60/host/month add-on",
    },
  },
  {
    id: "log-retention",
    label: "Log retention",
    middleware: {
      status: "check",
      description: "180 days included",
    },
    competitor: {
      status: "cross",
      description: "Hot/warm/cold, per-GiB-day",
    },
  },
  {
    id: "annual-commitment",
    label: "Annual commitment",
    middleware: {
      headline: "None",
      headlineStyle: "gradient",
    },
    competitor: {
      headline: "Mandatory 1–3 years",
      headlineStyle: "bold",
    },
  },
  {
    id: "free-trial",
    label: "Free trial",
    middleware: {
      status: "check",
      description: "14 days, no credit card",
    },
    competitor: {
      status: "cross",
      description: "15 days",
    },
  },
  {
    id: "dedicated-support",
    label: "Dedicated technical support",
    middleware: {
      status: "check",
      description: "All plans",
    },
    competitor: {
      status: "cross",
      description:
        "Tied to contract tier",
    },
  },
];

export const DYNATRACE_SWITCH_STATS: SwitchStatsSection = {
  tag: "What customers get",
  title: "The numbers after the switch",
  description:
    "Averages across customers who migrated from Dynatrace to Middleware.",
  stats: [
    {
      id: "troubleshooting-speed",
      value: "4",
      unit: "×",
      title: "Troubleshooting",
      description:
        "Faster mean-time-to-detect with unified signals on one timeline.",
    },
    {
      id: "cost-reduction",
      value: "60",
      unit: "%",
      title: "Cost Reduction",
      description:
        "Reduction in observability spend versus DPS enterprise contracts",
    },
    {
      id: "support-response",
      value: "1",
      unit: "hr",
      title: "Issue response SLA",
      description:
        "Critical-issue response SLA on every plan",
    },
    {
      id: "opsai-automation",
      value: "80",
      unit: "%",
      title: "Auto fixes with accuracy",
      description:
        "OpsAI resolves issues end-to-end, saving 60% of engineer time.",
    },
  ],
};

